import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const HIDE_AFTER_SCROLL_PX = 80; // 이 거리 이상 스크롤되어야 hide/show 트리거

// 한 세션 내에서 첫 등장 애니메이션을 이미 보여줬는지 여부.
// /life ↔ /todo 간 이동 시 컴포넌트가 재마운트되더라도 슬라이드 인을 반복하지 않도록 한다.
let hasIntroPlayed = false;

type ToggleKey = 'life' | 'todo' | 'daily';

const resolveKey = (pathname: string): ToggleKey => {
  if (pathname.startsWith('/todo')) return 'todo';
  if (pathname.startsWith('/daily')) return 'daily';
  return 'life';
};

const LifeTodoToggle: React.FC = () => {
  const location = useLocation();
  const active = resolveKey(location.pathname);

  // 첫 진입은 화면 밖(아래)에서 슬라이드 업, 페이지 이동 시에는 이미 보이는 상태로 시작.
  const [hidden, setHidden] = useState(!hasIntroPlayed);
  const lastScrollYRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);

  useEffect(() => {
    if (hasIntroPlayed) return;
    // 페인트 후 hidden 해제 → CSS transition으로 슬라이드 인
    const revealTimer = setTimeout(() => {
      setHidden(false);
      hasIntroPlayed = true;
    }, 50);
    return () => clearTimeout(revealTimer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const prev = lastScrollYRef.current;
      if (y > prev && y > HIDE_AFTER_SCROLL_PX) {
        setHidden(true);
      } else if (y < prev) {
        setHidden(false);
      }
      lastScrollYRef.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className='lifeTodoToggle'
      role='tablist'
      aria-label='Life / Todo 전환'
      style={{
        transform: hidden
          ? 'translate(-50%, calc(100% + 48px))'
          : 'translate(-50%, 0)',
        transition: 'transform 0.8s ease',
      }}
    >
      <Link
        to='/life'
        className={`lifeTodoToggleItem ${active === 'life' ? 'active' : ''}`}
        role='tab'
        aria-selected={active === 'life'}
      >
        Life
      </Link>
      <Link
        to='/todo'
        className={`lifeTodoToggleItem ${active === 'todo' ? 'active' : ''}`}
        role='tab'
        aria-selected={active === 'todo'}
      >
        Todo
      </Link>
      <Link
        to='/daily'
        className={`lifeTodoToggleItem lifeTodoToggleItem--daily ${active === 'daily' ? 'active' : ''}`}
        role='tab'
        aria-selected={active === 'daily'}
      >
        Daily
      </Link>
    </div>
  );
};

export default LifeTodoToggle;
